import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Button, Card, CardContent, CardActions, Grid, Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import api from '../api';

function Profile() {
  const [userInfo, setUserInfo] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showCongrats, setShowCongrats] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Check for payment success (from subscription-success redirect)
    if (params.get('payment') === 'success') {
      setPaymentStatus('success');
      setShowCongrats(true);
      window.history.replaceState({}, '', '/profile');
      fetchUserData();
    } else if (params.get('payment') === 'cancelled') {
      setPaymentStatus('cancelled');
      window.history.replaceState({}, '', '/profile');
      fetchUserData();
    } else if (params.get('from_portal') === 'true') {
      // Returning from Stripe portal - subscription may have changed
      console.log('[PORTAL] Returned from Stripe portal, refreshing data...');
      window.history.replaceState({}, '', '/profile');
      fetchUserData();
      // Show a message that changes may take a moment to reflect
      setTimeout(() => {
        setPaymentStatus('portal_return');
      }, 100);
    } else {
      fetchUserData();
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const [profileRes, subRes] = await Promise.all([
        api.get('/auth/profile/'),
        api.get('/auth/subscription/')
      ]);
      
      setUserInfo(profileRes.data);
      setSubscription(subRes.data);
      
      // Update localStorage
      localStorage.setItem('userInfo', JSON.stringify({
        username: profileRes.data.username,
        email: profileRes.data.email,
        subscription: profileRes.data.profile.subscription_plan
      }));
    } catch (err) {
      console.error('Failed to load profile data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    console.log('[UPGRADE] Button clicked, plan:', plan);
    setUpgrading(true);
    try {
      console.log('[UPGRADE] Making POST request to /auth/create-checkout-session/');
      const response = await api.post('/auth/create-checkout-session/', { plan });
      console.log('[UPGRADE] Response received:', response.data);
      
      if (response.data.checkout_url) {
            console.log('[UPGRADE] Redirecting to Stripe checkout:', response.data.checkout_url);
        // Store plan in localStorage so success page knows which plan was purchased
        localStorage.setItem('pendingUpgradePlan', plan);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('[UPGRADE] No checkout_url in response');
        alert('Invalid checkout response. Please try again.');
        setUpgrading(false);
      }
    } catch (err) {
      console.error('[UPGRADE] Error occurred:', err);
      console.error('[UPGRADE] Error response:', err.response);
      console.error('[UPGRADE] Error status:', err.response?.status);
      console.error('[UPGRADE] Error data:', err.response?.data);
      
      if (err.response?.status === 503) {
        const errorData = err.response.data;
        alert(`⚠️ Payment System Setup Required\n\n${errorData.message || 'Stripe Price IDs need to be configured in the backend .env file.'}\n\nFor now, you can test Pro features by manually updating your account.`);
      } else {
        alert('Failed to create checkout session. Please try again or contact support.');
      }
      setUpgrading(false);
    }
  };

  const handleDowngrade = async () => {
    const confirmDowngrade = window.confirm(
      `Are you sure you want to downgrade?\n\n` +
      `You'll lose access to ${currentPlan === 'enterprise' ? 'Enterprise' : 'Pro'} features ` +
      `at the end of your billing period.`
    );
    
    if (!confirmDowngrade) return;
    
    try {
      const response = await api.post('/auth/create-portal-session/');
      window.location.href = response.data.portal_url;
    } catch (err) {
      alert('Failed to open subscription management. Please contact support.');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await api.post('/auth/create-portal-session/');
      window.location.href = response.data.portal_url;
    } catch (err) {
      if (err.response?.status === 503) {
        alert('Subscription management is not yet configured. Please contact support@complain-ai.app to manage your subscription.');
      } else if (err.response?.status === 400) {
        alert('⚠️ Development Mode: Stripe portal requires real customer ID from production checkout. This will work once deployed with real Stripe keys.');
      } else {
        alert('Failed to open subscription portal. Please contact support@complain-ai.app for assistance.');
      }
    }
  };



  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const currentPlan = userInfo?.profile?.subscription_plan || 'free';
  
  // Debug logging
  console.log('[PROFILE] userInfo:', userInfo);
  console.log('[PROFILE] currentPlan:', currentPlan);
  console.log('[PROFILE] subscription_plan:', userInfo?.profile?.subscription_plan);

  const planFeatures = {
    pro: [
      '25 AI-powered disputes per month',
      'AI-enhanced letter generation',
      'Success prediction analysis',
      'Legal research & citations',
      'Auto-escalation to BBB/FTC',
      'Email & SMS notifications'
    ],
    enterprise: [
      'Unlimited AI-powered disputes',
      'All Pro features included',
      'Priority customer support',
      'Custom letter templates',
      'API access for integrations',
      'Advanced analytics dashboard'
    ]
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
      {/* Congratulations Dialog */}
      <Dialog 
        open={showCongrats} 
        onClose={() => setShowCongrats(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <StarIcon sx={{ fontSize: 60, color: '#ffd700' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            🎉 Congratulations!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
            You've upgraded to {currentPlan.toUpperCase()}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            You now have access to these premium features:
          </Typography>
          <List>
            {planFeatures[currentPlan]?.map((feature, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#00ff88' }} />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowCongrats(false);
              // Force full page reload to update all UI
              window.location.reload();
            }}
            sx={{ 
              bgcolor: 'white', 
              color: '#667eea',
              '&:hover': { bgcolor: '#f0f0f0' },
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            Get Started
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
        Account Settings
      </Typography>

      {paymentStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setPaymentStatus(null)}>
          Payment successful! Your subscription has been upgraded.
        </Alert>
      )}

      {paymentStatus === 'cancelled' && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setPaymentStatus(null)}>
          Payment cancelled. You can upgrade anytime.
        </Alert>
      )}

      {paymentStatus === 'portal_return' && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setPaymentStatus(null)}>
          Subscription updated. If you cancelled your subscription, you'll keep access until the end of your billing period.
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 4 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' } }}>Profile Information</Typography>
        <Box sx={{ mt: 2 }}>
          <Typography><strong>Username:</strong> {userInfo?.username}</Typography>
          <Typography><strong>Email:</strong> {userInfo?.email}</Typography>
          <Box sx={{ mt: 2 }}>
            <Chip 
              label={currentPlan.toUpperCase()} 
              color={currentPlan === 'free' ? 'default' : 'primary'}
              sx={{ mr: 1 }}
            />
            {currentPlan !== 'free' && (
              <Button size="small" onClick={handleManageSubscription}>
                Manage Subscription
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {subscription && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 4 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' } }}>Usage This Month</Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
            Disputes Used: {subscription.disputes_used} / {subscription.disputes_remaining === 'unlimited' ? '∞' : subscription.disputes_used + subscription.disputes_remaining}
          </Typography>
        </Paper>
      )}

      <Typography variant="h5" gutterBottom sx={{ mt: { xs: 3, md: 4 }, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
        Subscription Plans
      </Typography>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Free Plan */}
        <Grid item xs={12} md={4}>
          <Card variant={currentPlan === 'free' ? 'elevation' : 'outlined'} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom>Free</Typography>
              <Typography variant="h4" gutterBottom>$0</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> 1 free dispute
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> Basic AI letters
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              {currentPlan === 'free' ? (
                <Button fullWidth variant="outlined" disabled>Current Plan</Button>
              ) : (
                <Button fullWidth variant="outlined" onClick={handleDowngrade} color="warning">Downgrade to Free</Button>
              )}
            </CardActions>
          </Card>
        </Grid>

        {/* Pro Plan */}
        <Grid item xs={12} md={4}>
          <Card variant={currentPlan === 'pro' ? 'elevation' : 'outlined'} sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: currentPlan === 'pro' ? 2 : 1, borderColor: 'primary.main' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Chip label="POPULAR" color="primary" size="small" sx={{ mb: 1 }} />
              <Typography variant="h5" gutterBottom>Pro</Typography>
              <Typography variant="h4" gutterBottom>$9.99<Typography variant="caption">/month</Typography></Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> 10 letters/month
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> AI-enhanced letters
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> Auto-escalation (BBB/FTC)
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> Priority support
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              {currentPlan === 'pro' ? (
                <Button fullWidth variant="contained" disabled>Current Plan</Button>
              ) : currentPlan === 'enterprise' ? (
                <Button fullWidth variant="outlined" onClick={handleDowngrade} color="warning">Downgrade to Pro</Button>
              ) : (
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={() => handleUpgrade('pro')}
                  disabled={upgrading}
                >
                  {upgrading ? 'Processing...' : 'Upgrade to Pro'}
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>

        {/* Enterprise Plan */}
        <Grid item xs={12} md={4}>
          <Card variant={currentPlan === 'enterprise' ? 'elevation' : 'outlined'} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom>Enterprise</Typography>
              <Typography variant="h4" gutterBottom>$29.99<Typography variant="caption">/month</Typography></Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> Unlimited disputes
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> All Pro features
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> API access
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} /> Custom templates
                </Typography>
              </Box>
            </CardContent>
            <CardActions>
              {currentPlan === 'enterprise' ? (
                <Button fullWidth variant="contained" disabled>Current Plan</Button>
              ) : (
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={() => handleUpgrade('enterprise')}
                  disabled={upgrading}
                >
                  {upgrading ? 'Processing...' : 'Upgrade to Enterprise'}
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Profile;
